import classNames from "classnames";

import {
  isPresetRange,
  type PresetRange,
  TIME_RANGES,
  type TimeRangeMode,
} from "@/shared/domain/dashboards/DashboardDefinition";
import { Toggle, ToggleGroup } from "@/shared/foundation/primitives";

import { CustomRangePopover } from "./CustomRangePopover";
import styles from "./TimeRangeControl.module.css";

/**
 * The value space the toggle group ranges over. `"custom"` is deliberately
 * NOT backed by an item: parking the group's value there is how a custom
 * range renders with no preset selected, without the primitive needing a
 * no-selection mode. Because nothing carries it, the group can never emit it.
 */
type RangeSelection = PresetRange | "custom";

export interface TimeRangeControlProps {
  /** The active window. A preset and a custom range are the same input. */
  range: TimeRangeMode;

  /**
   * Fires ONLY when the range actually changes — pressing a preset today,
   * and applying the custom picker once that lands. Never fires for opening
   * a picker, editing it, or dismissing it.
   */
  onRangeChange: (range: TimeRangeMode) => void;

  /** Slot sizing injected by `DashboardHeader`, which owns header layout. */
  className?: string;
}

/**
 * The dashboard's time-range control: a Custom chip beside a segmented row of
 * presets.
 *
 * Holds no state of its own — everything rendered is derived from `range`,
 * which is what lets a future Cancel in the custom picker be a no-op by
 * construction rather than by cleanup.
 */
export function TimeRangeControl({
  range,
  onRangeChange,
  className,
}: TimeRangeControlProps) {
  const selection: RangeSelection = isPresetRange(range) ? range : "custom";

  return (
    <div className={classNames(styles.control, className)}>
      {/* A sibling, not a group item: pressing it opens a dialog rather than
       *  selecting, so it must not carry `aria-pressed`. */}
      <CustomRangePopover range={range} onApply={onRangeChange} />

      <ToggleGroup
        value={[selection]}
        onValueChange={(next) => {
          const [chosen] = next;
          if (chosen !== undefined && chosen !== "custom") {
            onRangeChange(chosen);
          }
        }}
        aria-label="Time range"
        size="md"
        className={styles.presetGroup}
      >
        {TIME_RANGES.map((tr) => (
          /* Both labels stay in the DOM and CSS picks one, so the swap
           * resolves during layout with no paint at the wrong size.
           * `aria-label` pins the accessible name to the full word either
           * way, so a screen reader never reads "30D". */
          <Toggle key={tr.value} value={tr.value} aria-label={tr.label}>
            <span aria-hidden="true" className={styles.labelFull}>
              {tr.label}
            </span>
            <span
              aria-hidden="true"
              className={styles.labelShort}
              title={tr.label}
            >
              {tr.short}
            </span>
          </Toggle>
        ))}
      </ToggleGroup>
    </div>
  );
}
